import React, { useState, FunctionComponent, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Box, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import Typography from '@mui/material/Typography';

// eslint-disable-next-line import/no-extraneous-dependencies
import {
  TreeItemCheckbox,
  TreeItemContent,
  TreeItemGroupTransition,
  TreeItemIcon,
  TreeItemIconContainer,
  TreeItemProvider,
  TreeItemRoot,
  useTreeItem,
} from '@mui/x-tree-view';

// MUI Icons
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityOffTwoToneIcon from '@mui/icons-material/VisibilityOffTwoTone';

// Openlayers
import OlLayerGroup from 'ol/layer/Group';
import OlBaseLayer from 'ol/layer/Base';

// Custom components
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import { checkIfLayerVisible, findLayer, flattenLayers } from '@/lib/olHelpers';
import TimeSeriesSlider from '@/ui/Slider/TimeSeriesSlider';
import OpacitySlider from '@/ui/Slider/OpacitySlider';
import LayerTreeItemChip from './LayerTreeItemChip';

// Types
import { ILayerTreeItem } from '@/@types/components/Map/Controls/Custom/LayerTree';
import LayerTreeItemSwiperSlider from './LayerTreeItemSwiperSlider';
import LayerTreeItemTimeCompanion from './LayerTreeItemTimeCompanion';

const LOCAL_STORAGE_KEY = 'opacity_layer_values';

const getStoredOpacityValue = (mapId: number, layerId: string): number | null => {
  try {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData && typeof parsedData === 'object' && parsedData[mapId]) {
        return parsedData[mapId][layerId] ?? null;
      }
    }
  } catch (error) {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    // console.error('Error parsing localStorage data:', error);
  }
  return null;
};

const saveOpacityValueToLocalStorage = (mapId: number, layerId: string, opacityValue: number) => {
  try {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    const parsedData = storedData ? JSON.parse(storedData) : {};

    if (typeof parsedData !== 'object') throw new Error('Stored data is not an object');

    parsedData[mapId] = {
      ...parsedData[mapId],
      [layerId]: opacityValue,
    };

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsedData));
  } catch (error) {
    // console.error('Error saving opacity value to localStorage:', error);
  }
};

const LayerTreeItem: FunctionComponent<ILayerTreeItem> = (props) => {
  const { t } = useTranslation();

  const mapContext = useContext(MapContext) as MapContextType;
  const { mapId, layerTime, setLayerTime, map } = mapContext;

  const { layer, onNodeSelect, currentZoomLevel, swipeState, onChangeSwipeState, allowSwiperSlider } = props;

  const id = layer.get('id');
  const title = layer.get('title');
  const layerExtent = layer.getExtent();
  const visible = checkIfLayerVisible(mapContext, layer);

  const hasOpacitySlider = layer.get('has_opacity_slider');

  const [opacityValue, setOpacityValue] = useState<number>(100);

  const show = title && title.length > 0;
  if (!show) {
    return null;
  }

  const isGroupLayer = layer instanceof OlLayerGroup;

  const directChildLayers = isGroupLayer ? layer.getLayers().getArray() : null;

  const getAllNonGroupChildLayers = (searchLayer: OlBaseLayer): OlBaseLayer[] => {
    if (!(searchLayer instanceof OlLayerGroup)) {
      return [searchLayer];
    }

    const layers = searchLayer.getLayers().getArray();
    return layers.flatMap((l) => getAllNonGroupChildLayers(l));
  };

  const allChildNonGroupLayers = isGroupLayer ? getAllNonGroupChildLayers(layer).filter((l) => !(l instanceof OlLayerGroup)) : [];

  const countTotal = allChildNonGroupLayers.length;
  const countVisible = allChildNonGroupLayers.filter((l) => checkIfLayerVisible(mapContext, l)).length;

  const children = directChildLayers?.map((l) => (
    <LayerTreeItem
      key={`lti-${l.get('id')}`}
      layer={l}
      onNodeSelect={onNodeSelect}
      currentZoomLevel={currentZoomLevel}
      swipeState={swipeState}
      allowSwiperSlider={allowSwiperSlider}
      onChangeSwipeState={onChangeSwipeState}
    />
  ));
  
  const allChildLayers = useMemo(() => isGroupLayer ? flattenLayers(directChildLayers, 5) : null, [isGroupLayer, directChildLayers]) 

  const childsVisible = allChildLayers ? allChildLayers.filter((x) => checkIfLayerVisible(mapContext, x) === true && !(x instanceof OlLayerGroup)).length : null;
  const childsHidden = allChildLayers ? allChildLayers.filter((x) => checkIfLayerVisible(mapContext, x) === false && !(x instanceof OlLayerGroup)).length : null;

  const groupChecked = childsVisible ? childsVisible > 0 : false;
  const groupIndeterminate = childsVisible && childsHidden ? childsVisible > 0 && childsHidden > 0 : false;

  const checked = isGroupLayer ? (groupIndeterminate ? true : groupChecked) : visible;

  const layersTime = layerTime;
  const time = layersTime && layersTime[id] ? layersTime[id] : undefined;

  const timeseries = layer.get('timeseries');
  const hasSlider = !!timeseries;

  const zoomable = layer.get('zoomable');

  const ticks = timeseries ? timeseries.length - 1 : 0;
  const hasTimeSeriesSlider = timeseries && timeseries.length > 0;

  const timeIndex = useMemo(() => {
    if (!timeseries) return 0;
    const idx = time ? timeseries.indexOf(time) : -1;
    return idx >= 0 ? idx : ticks;
  }, [timeseries, time, ticks]);

  // Falls back to the slider's own resolved (default) tick, not just the raw stored value, so
  // the label shows a year even before the user has ever touched the slider (e.g. right after
  // Reset, when nothing is in storage yet).
  const displayTime = hasTimeSeriesSlider ? timeseries[timeIndex] : undefined;

  const label = displayTime && hasSlider ? `${t(title)} (${displayTime})` : t(title);

  const visibleFromZoomLevel = layer.get('visible_min_zoom');
  const visibleToZoomLevel = layer.get('visible_max_zoom');

  // const hasVisibilityLimits = visibleFromZoomLevel || visibleToZoomLevel;

  const lowBounderyOk = currentZoomLevel && visibleFromZoomLevel ? currentZoomLevel >= visibleFromZoomLevel : true;
  const upperBoundaryOk = currentZoomLevel && visibleToZoomLevel ? currentZoomLevel <= visibleToZoomLevel : true;
  const dataVisible = lowBounderyOk && upperBoundaryOk;

  const noFeatures = layer.get('no_features') === true;

  // if (hasVisibilityLimits) {
  //   console.log(id, dataVisible);
  // }

  const handleValueLabel = (x: number) => {
    if (timeseries && timeseries.length > x) {
      return timeseries[x];
    }
    return '?';
  };

  const handleChangeCommitted = (event: React.SyntheticEvent | Event, value: number | Array<number>) => {
    event.stopPropagation();
    const newTime = handleValueLabel(value as number);
    if (id && newTime) {
      setLayerTime(id, newTime);
    }
  };

  const layerSwipeState = useMemo(() => {
    if (swipeState) {
      return swipeState[layer.get('id')];
    }
    return undefined;
  }, [swipeState, layer]);

  // "Compare" companion: lets a timeseries layer be shown at a second, independent point in
  // time on the opposite side of the swipe divider. Side is always mirrored from this layer's
  // own swipe state - it is never chosen independently on the companion itself.
  const companionId = `${id}::cmp`;

  const companionChecked = mapContext.layerVisibility[companionId] !== false;

  const companionSide: 'L' | 'R' | undefined = layerSwipeState === 'L' ? 'R' : layerSwipeState === 'R' ? 'L' : undefined;

  const companionActive = checked && companionChecked && companionSide !== undefined;

  const companionTime = layersTime?.[companionId];

  const companionTimeIndex = useMemo(() => {
    if (!timeseries) return 0;
    const idx = companionTime ? timeseries.indexOf(companionTime) : -1;
    return idx >= 0 ? idx : ticks;
  }, [timeseries, companionTime, ticks]);

  const companionDisplayTime = hasTimeSeriesSlider ? timeseries[companionTimeIndex] : undefined;

  const wasCompanionActiveRef = React.useRef(companionActive);

  useEffect(() => {
    if (companionActive && !wasCompanionActiveRef.current && !companionTime && timeseries) {
      const baseIndex = timeIndex >= 0 ? timeIndex : ticks;
      const defaultIndex = baseIndex + 1 <= ticks ? baseIndex + 1 : baseIndex - 1 >= 0 ? baseIndex - 1 : baseIndex;
      const newTime = timeseries[defaultIndex];
      if (newTime) {
        setLayerTime(companionId, newTime);
      }
    }
    wasCompanionActiveRef.current = companionActive;
  }, [companionActive]);

  // Companion mounts its own OL layer fresh, so it starts at default opacity even if Row A's
  // was customized earlier - sync it once it appears.
  useEffect(() => {
    if (companionActive && map) {
      const companionLayer = findLayer(map, companionId);
      companionLayer?.setOpacity(opacityValue / 100);
    }
  }, [companionActive, map, companionId, opacityValue]);

  const handleToggleCompanionChecked = (evt: React.SyntheticEvent) => {
    evt.stopPropagation();
    mapContext.setLayerVisibility(companionId, !companionChecked);
  };

  const handleCompanionChangeCommitted = (event: React.SyntheticEvent | Event, value: number | Array<number>) => {
    event.stopPropagation();
    const newTime = handleValueLabel(value as number);
    if (newTime && newTime !== '?') {
      setLayerTime(companionId, newTime);
    }
  };

  const handleChangeOpacity = (event: Event, value: number | Array<number>) => {
    event.stopPropagation();
    if (mapContext && map && typeof value === 'number') {
      const mapLayer = findLayer(map, id);
      mapLayer?.setOpacity(value / 100);
      // Companion is the same data at a second point in time - keep it visually matched to Row A.
      const companionLayer = findLayer(map, companionId);
      companionLayer?.setOpacity(value / 100);
      setOpacityValue(value);

      if (mapId && id) {
        saveOpacityValueToLocalStorage(mapId, id, value);
      }
    }
  };

  const handleZoomToLayer = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.stopPropagation();
    if (mapContext && map && layerExtent) {
      const view = map.getView();
      view.fit(layerExtent, { padding: [20, 20, 20, 220], duration: 1000 });
    }
  };

  const { getRootProps, getContentProps, getIconContainerProps, getCheckboxProps, getLabelProps, getGroupTransitionProps, status } =
    useTreeItem({ id, itemId: id, children, label });

  useEffect(() => {
    if (mapId && id) {
      const storedOpacity = getStoredOpacityValue(mapId, id);
      if (storedOpacity !== null && mapContext && map) {
        setOpacityValue(storedOpacity);
        const mapLayer = findLayer(map, id);
        mapLayer?.setOpacity(storedOpacity / 100);
      }
    }
  }, [mapId, id, map]);

  const handleChangeSwipeStatus = (event: Event | React.SyntheticEvent<Element, Event>, value: 'L' | null | 'R') => {
    if (onChangeSwipeState) {
      onChangeSwipeState(id, value);
      if (directChildLayers) {
        flattenLayers(directChildLayers, 6).forEach((lay) => onChangeSwipeState(lay.get('id'), value));
      }
    }
  };

  const getVisibleLeafSwipeStates = () => {
    if (!isGroupLayer) return [];

    return allChildNonGroupLayers
      .filter((lay) => mapContext.layerVisibility[lay.get('id')] !== false) // only visible leaves
      .map((lay) => swipeState?.[lay.get('id')]);
  };

  const leafSwipeStates = getVisibleLeafSwipeStates();

  // check if all visible leaves have the same state
  const allLeavesSame = leafSwipeStates.length > 0 && leafSwipeStates.every((s) => s === leafSwipeStates[0]);

  // if they are all the same AND differ from the parent → sync parent
  useEffect(() => {
    if (isGroupLayer && allLeavesSame && leafSwipeStates[0] !== layerSwipeState) {
      handleChangeSwipeStatus(new Event(''), leafSwipeStates[0] ?? null);
    }
  }, [allLeavesSame, leafSwipeStates, isGroupLayer, layerSwipeState]);

  const shouldHideThumb =
    isGroupLayer &&
    !allChildNonGroupLayers
      .map((lay) => lay.get('id') as string)
      .filter((layerId) => mapContext.layerVisibility[layerId] !== false)
      .every((layerId) => swipeState && swipeState[layerId] === layerSwipeState) &&
    !(allLeavesSame && leafSwipeStates[0] !== layerSwipeState);

  const clickFromSliderRef = React.useRef(false);

  return (
    <>
    {/* @ts-ignore i am not smart enough to fix this one unfortunately */}
    <TreeItemProvider itemId={id}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent
          tabIndex={noFeatures ? -1 : 0}
          sx={{
            gap: 0,
            p: 0,
            pl: 'calc(var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))',
            cursor: noFeatures ? 'default' : 'pointer',
            '&:hover': { backgroundColor: noFeatures ? 'transparent' : undefined },
          }}
          {...getContentProps()}
          onMouseDown={(evt) => {
            if ((evt.target as HTMLElement).closest('.MuiSlider-root')) {
              clickFromSliderRef.current = true;
            }
          }}
          onClick={(evt) => {
            if (clickFromSliderRef.current) {
              clickFromSliderRef.current = false;
              // ignore click if it started in slider hacky but there was no better solution
              return;
            }
            if (noFeatures) {
              evt.stopPropagation();
              return;
            }
            onNodeSelect(evt, id);
          }}
        >
          {noFeatures ? (
            <Tooltip title={t('Ne određuje se.') as string}>
              <Box sx={{ padding: '9px', display: 'flex', alignItems: 'center' }}>
                <LayersClearIcon sx={{ fontSize: 24, color: '#999' }} />
              </Box>
            </Tooltip>
          ) : (
            <TreeItemCheckbox
              {...getCheckboxProps()}
              sx={{ padding: '9px' }}
              color="primary"
              checked={checked}
              indeterminate={groupIndeterminate}
            />
          )}
          <Typography
            sx={{
              width: '100%',
              fontWeight: isGroupLayer ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
            component="div"
            {...getLabelProps()}
          >
            {t(label)}
            {isGroupLayer ? <LayerTreeItemChip countVisible={countVisible} countTotal={countTotal} /> : null}
          </Typography>
          {hasTimeSeriesSlider && (
            <TimeSeriesSlider
              ticks={ticks}
              value={timeIndex}
              checked={checked}
              handleValueLabel={handleValueLabel}
              handleChangeCommited={handleChangeCommitted}
            />
          )}
          {!allowSwiperSlider ? (
            <Box display="flex" maxWidth="7rem">
              {hasOpacitySlider && <OpacitySlider value={opacityValue} checked={checked} handleChange={handleChangeOpacity} />}
              {dataVisible && zoomable && (
                <IconButton onClick={handleZoomToLayer} color="primary" disabled={!layerExtent || !checked} edge="start" size="small">
                  <LocationOnIcon />
                </IconButton>
              )}
              {!dataVisible && (
                <Tooltip title={t('tooltips.zoom_level_helper_text') as string}>
                  <IconButton disableTouchRipple disableRipple size="small">
                    <VisibilityOffTwoToneIcon sx={{ color: 'grey' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : null}
          {allowSwiperSlider ? (
            <LayerTreeItemSwiperSlider
              swipeState={layerSwipeState}
              onChangeSwipeState={handleChangeSwipeStatus}
              marginRight={isGroupLayer ? '20px' : '44px'}
              isThumbDisabled={!checked}
              hideThumb={shouldHideThumb}
            />
          ) : null}
          {status.expandable && (
            <TreeItemIconContainer
              sx={{ marginRight: '8px' }}
              {...getIconContainerProps()}
              onClick={(evt) => {
                evt.stopPropagation();
                getContentProps().onClick(evt);
              }}
            >
              <TreeItemIcon status={status} />
            </TreeItemIconContainer>
          )}
        </TreeItemContent>
        {directChildLayers && <TreeItemGroupTransition {...getGroupTransitionProps()}>{children}</TreeItemGroupTransition>}
      </TreeItemRoot>
    </TreeItemProvider>
    {hasTimeSeriesSlider && allowSwiperSlider && (
      <LayerTreeItemTimeCompanion
        companionId={companionId}
        checked={companionChecked}
        onToggleChecked={handleToggleCompanionChecked}
        side={companionSide}
        rowChecked={checked}
        active={companionActive}
        displayTime={companionDisplayTime}
        ticks={ticks}
        value={companionTimeIndex}
        handleValueLabel={handleValueLabel}
        handleChangeCommitted={handleCompanionChangeCommitted}
        marginRight={isGroupLayer ? '20px' : '44px'}
      />
    )}
    </>
  );
};

export default LayerTreeItem;
